import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Paper,
    Typography,
    Box,
    Stack,
    Divider,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { useBootstrap } from '../../context/BootstrapContext';
import { usePermissao } from '../../security/usePermissao';
import { updateClienteBranding } from '../../infra/api/Clientes';
import { FUNDO_ESCURO_MARCA } from '../../utils/marca';

// A logo/background viajam dentro do bootstrap (baixado em todo login), então
// limitamos o tamanho pra não inchar o payload.
const MAX_IMAGE_BYTES = 500 * 1024; // 500 KB

const fileToDataURL = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const Field = ({ label, value }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
            {value || '—'}
        </Typography>
    </Box>
);

// Preview da imagem; se `canEdit`, vira clicável (troca o arquivo) e mostra
// os botões Trocar/Remover. `fundo` ('claro' | 'escuro'): a prévia da logo aparece
// sobre o fundo em que ela vai ser usada, mais larga porque logos são horizontais.
const EditableImage = ({ label, ajuda, src, canEdit, onPick, onClear, fundo }) => {
    const theme = useTheme();
    const corFundo = fundo === 'escuro' ? FUNDO_ESCURO_MARCA : fundo === 'claro' ? '#FFFFFF' : undefined;
    const inputRef = useRef(null);
    return (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5 }}
            >
                {label}
            </Typography>
            <Box
                onClick={canEdit ? () => inputRef.current?.click() : undefined}
                sx={{
                    position: 'relative',
                    width: fundo ? 160 : 72,
                    height: 72,
                    p: fundo ? 1 : 0,
                    boxSizing: 'border-box',
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: fundo === 'escuro' ? 'rgba(255,255,255,0.25)' : 'divider',
                    bgcolor: corFundo,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    cursor: canEdit ? 'pointer' : 'default',
                    '&:hover .edit-overlay': { opacity: canEdit ? 1 : 0 },
                }}
            >
                {src ? (
                    <Box
                        component="img"
                        src={src}
                        alt={label}
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                        }}
                    />
                ) : (
                    <Lucide.Image color={fundo === 'escuro' ? 'rgba(255,255,255,0.4)' : theme.palette.action.disabled} size={18} />
                )}
                {canEdit && (
                    <Box
                        className="edit-overlay"
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(0,0,0,0.45)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.15s',
                        }}
                    >
                        <Lucide.Pencil size={16} />
                    </Box>
                )}
            </Box>
            {ajuda && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, maxWidth: 200 }}>
                    {ajuda}
                </Typography>
            )}
            {canEdit && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={onPick}
                    />
                    <Button
                        size="small"
                        startIcon={<Lucide.Upload size={14} />}
                        onClick={() => inputRef.current?.click()}
                    >
                        Trocar
                    </Button>
                    {src && (
                        <Button size="small" color="error" onClick={onClear}>
                            Remover
                        </Button>
                    )}
                </Stack>
            )}
        </Box>
    );
};

const CompanyInfoSection = () => {
    const theme = useTheme();
    const { bootstrap, refresh } = useBootstrap();
    const { pode } = usePermissao();
    const client = bootstrap?.client || {};
    const canEdit = pode('empresa.editar');

    const [logo, setLogo] = useState(client.logoBase64 || null);
    const [logoEscuro, setLogoEscuro] = useState(client.logoEscuroBase64 || null);
    const [emailBg, setEmailBg] = useState(
        client.emailBackgroundBase64 || null
    );
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState(null);

    // Ressincroniza com o bootstrap (ex.: após um refresh) quando o branding muda.
    useEffect(() => {
        setLogo(client.logoBase64 || null);
        setLogoEscuro(client.logoEscuroBase64 || null);
        setEmailBg(client.emailBackgroundBase64 || null);
    }, [client.logoBase64, client.logoEscuroBase64, client.emailBackgroundBase64]);

    const dirty = useMemo(
        () =>
            logo !== (client.logoBase64 || null) ||
            logoEscuro !== (client.logoEscuroBase64 || null) ||
            emailBg !== (client.emailBackgroundBase64 || null),
        [logo, logoEscuro, emailBg, client.logoBase64, client.logoEscuroBase64, client.emailBackgroundBase64]
    );

    const address = [client.street, client.number, client.complement]
        .filter(Boolean)
        .join(', ');
    const cityState = [client.city, client.state].filter(Boolean).join(' - ');

    const handlePick = (setter) => async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setSnack({ type: 'error', msg: 'Selecione um arquivo de imagem.' });
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setSnack({ type: 'error', msg: 'Imagem muito grande (máx. 500 KB).' });
            return;
        }
        try {
            setter(await fileToDataURL(file));
        } catch {
            setSnack({ type: 'error', msg: 'Erro ao ler a imagem.' });
        }
    };

    const handleSave = async () => {
        if (!client.id) return;
        setSaving(true);
        try {
            await updateClienteBranding(client.id, {
                logoBase64: logo,
                // "" remove; ausente (null) faria o IoT hub manter a gravada.
                logoEscuroBase64: logoEscuro || '',
                emailBackgroundBase64: emailBg,
            });
            // Recarrega o bootstrap pra a logo nova aparecer no app na hora.
            await refresh(true);
            setSnack({ type: 'success', msg: 'Branding atualizado com sucesso.' });
        } catch {
            setSnack({ type: 'error', msg: 'Falha ao salvar o branding.' });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setLogo(client.logoBase64 || null);
        setLogoEscuro(client.logoEscuroBase64 || null);
        setEmailBg(client.emailBackgroundBase64 || null);
    };

    return (
        <Paper
            sx={{
                p: 2.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                boxShadow: theme.shadows[2],
            }}
        >
            <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 500, color: theme.palette.text.primary }}
            >
                Dados da Empresa
            </Typography>

            <Stack spacing={2} sx={{ mt: 1 }}>
                <Field label="Razão Social" value={client.name} />
                <Box display="flex" gap={3}>
                    <Field label="Documento" value={client.document} />
                    <Field label="E-mail" value={client.email} />
                </Box>
                <Field label="Endereço" value={address} />
                <Field label="Cidade / UF" value={cityState} />

                <Divider sx={{ my: 1 }} />

                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    flexWrap="wrap"
                >
                    <Typography variant="subtitle2" color="text.secondary">
                        Branding
                    </Typography>
                    {canEdit && (
                        <Typography variant="caption" color="text.secondary">
                            PNG/JPG até 500 KB
                        </Typography>
                    )}
                </Box>

                <Box display="flex" gap={3} flexWrap="wrap">
                    <EditableImage
                        label="Logo para fundo claro"
                        ajuda="Aparece sobre fundo branco."
                        fundo="claro"
                        src={logo}
                        canEdit={canEdit}
                        onPick={handlePick(setLogo)}
                        onClear={() => setLogo(null)}
                    />
                    <EditableImage
                        label="Logo para fundo escuro"
                        ajuda={logoEscuro ? 'Aparece sobre a barra escura e no tema escuro.' : 'Opcional. Sem ela, usamos a logo para fundo claro.'}
                        fundo="escuro"
                        src={logoEscuro}
                        canEdit={canEdit}
                        onPick={handlePick(setLogoEscuro)}
                        onClear={() => setLogoEscuro(null)}
                    />
                    <EditableImage
                        label="Background do e-mail"
                        src={emailBg}
                        canEdit={canEdit}
                        onPick={handlePick(setEmailBg)}
                        onClear={() => setEmailBg(null)}
                    />
                </Box>

                {canEdit && (
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Button
                            variant="contained"
                            size="small"
                            disabled={!dirty || saving}
                            onClick={handleSave}
                            startIcon={
                                saving ? (
                                    <CircularProgress size={14} color="inherit" />
                                ) : (
                                    <Lucide.Save size={16} />
                                )
                            }
                        >
                            Salvar branding
                        </Button>
                        {dirty && !saving && (
                            <Button
                                variant="text"
                                size="small"
                                color="inherit"
                                onClick={handleReset}
                            >
                                Descartar
                            </Button>
                        )}
                    </Stack>
                )}
            </Stack>

            <Snackbar
                open={Boolean(snack)}
                autoHideDuration={4000}
                onClose={() => setSnack(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snack?.type || 'info'}
                    variant="filled"
                    onClose={() => setSnack(null)}
                    sx={{ width: '100%' }}
                >
                    {snack?.msg}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default CompanyInfoSection;
